import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import type { City } from './types'

interface LocationState {
  currentCity: City | null;
  setCurrentCity: (city: City | null) => void;
}

/**
 * Validates the structure of stored city data
 * Prevents XSS by ensuring data structure is correct
 */
const validateCityStructure = (city: unknown): city is City => {
  if (!city || typeof city !== 'object') return false

  const cityObj = city as Record<string, unknown>

  // Check required fields
  if (typeof cityObj.id !== 'string' || !cityObj.id) return false
  if (typeof cityObj.name !== 'string' || !cityObj.name) return false
  // slug is optional - might not be present in stored data
  if (cityObj.slug !== undefined && typeof cityObj.slug !== 'string') return false

  // Validate defaultShop if present
  if (cityObj.defaultShop !== undefined && cityObj.defaultShop !== null) {
    if (typeof cityObj.defaultShop !== 'object') return false
    const shop = cityObj.defaultShop as Record<string, unknown>
    if (typeof shop.id !== 'string' || !shop.id) return false
  }

  // Sanitize: ensure name doesn't contain dangerous characters
  // Basic XSS prevention - check for script tags and other dangerous patterns
  const name = cityObj.name as string
  if (/<script|javascript:|onerror|onload=/i.test(name)) {
    return false
  }

  return true
}

export const useLocationStore = create(
  persist<LocationState>(
    (set) => ({
      currentCity: null,
      setCurrentCity: (city) => set({ currentCity: city }),
    }),
    {
      name: 'current-city',
      onRehydrateStorage: () => (state) => {
        // Validate stored city structure on rehydration
        if (state?.currentCity) {
          console.log('Rehydrating city from localStorage:', state.currentCity)
          if (!validateCityStructure(state.currentCity)) {
            console.warn('Invalid city structure in localStorage, resetting:', state.currentCity)
            state.setCurrentCity(null)
          }
          else {
            console.log('City structure validation passed:', state.currentCity)
          }
        }
      },
    },
  ),
)
